import { msg } from "@lingui/core/macro";
import { createRoute, useNavigate, useSearch } from "@tanstack/solid-router";
import { createMemo, createSignal, For, onMount, Show } from "solid-js";
import { FolderOpen, ShoppingCart, Trash2 } from "@fleet/icons";
import {
  addPurchaseOrderLineMutation,
  coercePoState,
  confirmPurchaseOrderMutation,
  createPurchaseOrderMutation,
  createVendorMutation,
  deletePurchaseOrderLineMutation,
  deletePurchaseOrderMutation,
  fromMinorQty,
  getLocationsQuery,
  getProductsQuery,
  getPurchaseOrderLinesQuery,
  getPurchaseOrdersQuery,
  getVendorsQuery,
  parseQtyInput,
  pendingQty,
  queryKeys,
  receivePurchaseOrderMutation,
} from "@stockpilot/shared";
import { createPowerSyncQuery } from "@stockpilot/shared/solid";
import { appRoute } from "./route";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useI18n } from "@/providers/i18n-provider";
import { useOrgId } from "@/features/inventory/use-org-id";
import { useResetOnOrgSwitch } from "@/features/inventory/use-reset-on-org-switch";
import { takeInitialSearchParam } from "@/lib/initial-search";
import { createEntityResolution } from "@/lib/use-entity-resolution";
import { NotFoundState } from "@/components/ui/not-found-state";
import { PageContainer, PageHeader } from "@fleet/ui";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { EmptyOrdersIllustration } from "@/components/ui/illustrations";
import { Select } from "@/components/ui/select";
import { SwipeRow, SwipeTableRow } from "@/components/ui/swipe-row";
import { showToast } from "@/components/ui/toast";
import type {
  LocationRecord,
  ProductRecord,
  PurchaseOrderLineRecord,
  PurchaseOrderRecord,
  VendorRecord,
} from "@stockpilot/powersync";

function PurchasesPage() {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const orgId = useOrgId();
  const search = useSearch({ from: "/_app/purchases" });
  const navigate = useNavigate();

  // Vendor form.
  const [vendorName, setVendorName] = createSignal("");

  // Purchase order form.
  const [poVendor, setPoVendor] = createSignal("");
  const [poLocation, setPoLocation] = createSignal("");
  const [poReference, setPoReference] = createSignal("");

  // The open order is deep-linkable: the URL (`?po=<id>`) is the source of
  // truth. Pasting/sharing `/purchases?po=<id>` opens exactly that order;
  // reloading reconstructs it (idempotent). `setOpenPoId` only writes the URL.
  const openPoId = (): string | null => search().po ?? null;
  const setOpenPoId = (id: string | null) => {
    void navigate({
      to: "/purchases",
      search: id ? { po: id } : {},
      replace: true,
    });
  };

  // Guard for the router's deferred flush: if boot lost the `?po=` from the
  // initial deep link, reapply it synchronously from the snapshot.
  onMount(() => {
    const initialPo = takeInitialSearchParam("po", "/purchases");
    if (initialPo && !openPoId()) {
      setOpenPoId(initialPo);
    }
  });

  // Multi-organization isolation: when changing company, the open purchase order
  // belongs to the previous org → discard the pointer.
  useResetOnOrgSwitch(() => setOpenPoId(null));

  // Line form.
  const [lineProduct, setLineProduct] = createSignal("");
  const [lineQty, setLineQty] = createSignal("");
  const [linePrice, setLinePrice] = createSignal("");

  const products = createPowerSyncQuery<ProductRecord>({
    queryKey: () => queryKeys.inventory.products(orgId()),
    queryFn: () => getProductsQuery(drizzle()!, orgId()),
    db: () => db(),
    enabled: () => Boolean(drizzle() && orgId()),
  });

  const locations = createPowerSyncQuery<LocationRecord>({
    queryKey: () => queryKeys.inventory.locations(orgId()),
    queryFn: () => getLocationsQuery(drizzle()!, orgId()),
    db: () => db(),
    enabled: () => Boolean(drizzle() && orgId()),
  });

  const vendors = createPowerSyncQuery<VendorRecord>({
    queryKey: () => queryKeys.inventory.vendors(orgId()),
    queryFn: () => getVendorsQuery(drizzle()!, orgId()),
    db: () => db(),
    enabled: () => Boolean(drizzle() && orgId()),
  });

  const orders = createPowerSyncQuery<PurchaseOrderRecord>({
    queryKey: () => queryKeys.inventory.purchaseOrders(orgId()),
    queryFn: () => getPurchaseOrdersQuery(drizzle()!, orgId()),
    db: () => db(),
    enabled: () => Boolean(drizzle() && orgId()),
  });

  const openLines = createPowerSyncQuery<PurchaseOrderLineRecord>({
    queryKey: () => queryKeys.inventory.purchaseOrderLines(openPoId() ?? undefined),
    queryFn: () => getPurchaseOrderLinesQuery(drizzle()!, openPoId()!),
    db: () => db(),
    enabled: () => Boolean(drizzle() && openPoId()),
  });

  const productName = createMemo(() => {
    const map = new Map((products.data ?? []).map((p) => [p.id, p.name]));
    return (id: string) => map.get(id) ?? id;
  });
  const vendorName2 = createMemo(() => {
    const map = new Map((vendors.data ?? []).map((v) => [v.id, v.name]));
    return (id: string | null) => (id ? (map.get(id) ?? id) : "—");
  });

  const openPo = createMemo(() => (orders.data ?? []).find((p) => p.id === openPoId()));

  // Resolves the deep-linked order (`?po=<id>`): present / loading /
  // not-found / offline. Avoids silently rendering the view (without feedback)
  // when the order does not exist, was deleted, or the user lost access (PowerSync
  // sync rules do not download rows without permission → absent after sync = no access).
  // Never leaves a loader hanging: the timeout safety net guarantees it.
  const poResolution = createEntityResolution({
    db: () => db(),
    hasEntity: () => Boolean(openPo()),
    isPending: () => orders.isLoading,
  });

  const stateLabel = (state: string | null) => {
    switch (coercePoState(state)) {
      case "draft": {
        return t(msg`Draft`);
      }
      case "confirmed": {
        return t(msg`Confirmed`);
      }
      case "received": {
        return t(msg`Received`);
      }
      default: {
        return state ?? "";
      }
    }
  };

  async function handleCreateVendor(e: Event) {
    e.preventDefault();
    const d = drizzle();
    if (!d || vendorName().trim() === "") {
      return;
    }
    await createVendorMutation(d, orgId(), { name: vendorName() });
    setVendorName("");
  }

  async function handleCreatePo(e: Event) {
    e.preventDefault();
    const d = drizzle();
    if (!d) {
      return;
    }
    const id = await createPurchaseOrderMutation(d, orgId(), {
      vendorId: poVendor() || null,
      dstLocationId: poLocation() || null,
      reference: poReference().trim() || null,
    });
    setPoReference("");
    setOpenPoId(id);
  }

  async function handleAddLine() {
    const d = drizzle();
    const po = openPo();
    const qty = parseQtyInput(lineQty());
    // The price is per whole unit, in minor money units (×1000 likewise).
    const price = parseQtyInput(linePrice());
    if (!d || !po || !lineProduct() || qty === null || qty <= 0) {
      return;
    }
    await addPurchaseOrderLineMutation(d, orgId(), po.id, lineProduct(), qty, price ?? 0);
    setLineProduct("");
    setLineQty("");
    setLinePrice("");
  }

  async function handleConfirm() {
    const d = drizzle();
    const po = openPo();
    if (!d || !po) {
      return;
    }
    // `confirmPurchaseOrderMutation` may THROW (verified phantom UPDATE,
    // see `@stockpilot/shared/inventory/mutations`): without this catch, a
    // rejection would surface as an unhandled rejection instead of notifying the user.
    try {
      await confirmPurchaseOrderMutation(d, po.id, { id: po.id, state: po.state });
    } catch {
      showToast(t(msg`Could not confirm the purchase order`), "error");
    }
  }

  async function handleReceiveAll() {
    const d = drizzle();
    const po = openPo();
    if (!d || !po) {
      return;
    }
    const lines = (openLines.data ?? []).map((l) => ({
      id: l.id,
      product_id: l.product_id,
      qty: l.qty,
      price: l.price,
      received_qty: l.received_qty,
    }));
    // CC-7: blocking receipt (await). Without `requested` = receive all
    // pending. `receivePurchaseOrderMutation` may THROW, see above.
    try {
      await receivePurchaseOrderMutation(
        d,
        orgId(),
        {
          id: po.id,
          state: po.state,
          warehouse_id: po.warehouse_id,
          dst_location_id: po.dst_location_id,
          reference: po.reference,
        },
        lines,
      );
    } catch {
      showToast(t(msg`Could not receive the purchase order`), "error");
    }
  }

  async function handleReceiveLine(lineId: string, qtyRaw: string) {
    const d = drizzle();
    const po = openPo();
    const qty = parseQtyInput(qtyRaw);
    if (!d || !po || qty === null || qty <= 0) {
      return;
    }
    const lines = (openLines.data ?? []).map((l) => ({
      id: l.id,
      product_id: l.product_id,
      qty: l.qty,
      price: l.price,
      received_qty: l.received_qty,
    }));
    try {
      await receivePurchaseOrderMutation(
        d,
        orgId(),
        {
          id: po.id,
          state: po.state,
          warehouse_id: po.warehouse_id,
          dst_location_id: po.dst_location_id,
          reference: po.reference,
        },
        lines,
        new Map([[lineId, qty]]),
      );
    } catch {
      showToast(t(msg`Could not receive the purchase order line`), "error");
    }
  }

  async function handleDeleteLine(id: string) {
    const d = drizzle();
    if (!d) {
      return;
    }
    await deletePurchaseOrderLineMutation(d, id);
  }

  async function handleDeletePo(id: string) {
    const d = drizzle();
    if (!d) {
      return;
    }
    await deletePurchaseOrderMutation(d, id);
    if (openPoId() === id) {
      setOpenPoId(null);
    }
  }

  return (
    <PageContainer>
      <PageHeader
        title={t(msg`Purchases`)}
        description={t(msg`Vendors and purchase orders with receipts and backorders.`)}
        icon={ShoppingCart}
      />

      {/* Vendors */}
      <form
        class="mt-6 flex flex-wrap items-end gap-2 rounded-md border border-border p-4"
        onSubmit={handleCreateVendor}
        data-testid="vendor-form"
      >
        <div class="flex flex-col gap-1">
          <label class="text-xs text-muted-foreground">{t(msg`New vendor`)}</label>
          <input
            class="w-56 h-10 rounded-md border border-border bg-field-background px-3 text-sm"
            placeholder={t(msg`Vendor name`)}
            value={vendorName()}
            onInput={(e) => setVendorName(e.currentTarget.value)}
            data-testid="vendor-name-input"
          />
        </div>
        <Button type="submit" disabled={!drizzle()} data-testid="vendor-add-button">
          {t(msg`Add vendor`)}
        </Button>
        <span class="text-xs text-muted-foreground" data-testid="vendor-count">
          {t(msg`Vendors`)}: {(vendors.data ?? []).length}
        </span>
      </form>

      {/* New purchase order */}
      <form
        class="mt-4 flex flex-wrap items-end gap-2 rounded-md border border-border p-4"
        onSubmit={handleCreatePo}
        data-testid="po-form"
      >
        <div class="flex flex-col gap-1">
          <label class="text-xs text-muted-foreground">{t(msg`Vendor`)}</label>
          <Select
            class="w-44"
            value={poVendor()}
            onChange={(v) => setPoVendor(v)}
            data-testid="po-vendor-select"
            aria-label={t(msg`Vendor`)}
            placeholder={t(msg`Select vendor`)}
            options={(vendors.data ?? []).map((v) => ({ value: v.id, label: v.name }))}
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs text-muted-foreground">{t(msg`Destination location`)}</label>
          <Select
            class="w-44"
            value={poLocation()}
            onChange={(v) => setPoLocation(v)}
            data-testid="po-location-select"
            aria-label={t(msg`Destination location`)}
            placeholder={t(msg`Select location`)}
            options={(locations.data ?? []).map((l) => ({ value: l.id, label: l.name }))}
            emptyAction={{
              label: t(msg`Add a location`),
              description: t(msg`No locations yet`),
              href: "/warehouses",
            }}
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs text-muted-foreground">{t(msg`Reference`)}</label>
          <input
            class="w-40 h-10 rounded-md border border-border bg-field-background px-3 text-sm"
            placeholder={t(msg`Optional`)}
            value={poReference()}
            onInput={(e) => setPoReference(e.currentTarget.value)}
            data-testid="po-reference-input"
          />
        </div>
        <Button type="submit" disabled={!drizzle()} data-testid="po-create-button">
          {t(msg`Create order`)}
        </Button>
      </form>

      {/* Unresolvable deep-link: URL requests an order that does not exist / no access
          / offline. We show feedback instead of a silent empty panel. */}
      <Show when={openPoId() && poResolution() !== "ready" && poResolution() !== "loading"}>
        <div class="mt-6" data-testid="po-not-found">
          <NotFoundState
            variant={poResolution() === "offline" ? "offline" : "not-found"}
            title={poResolution() === "offline" ? undefined : t(msg`Purchase order not found`)}
            description={
              poResolution() === "offline"
                ? undefined
                : t(msg`This order may have been deleted, or you don't have access to it.`)
            }
            primaryAction={{
              label: t(msg`Back to purchases`),
              onClick: () => {
                setOpenPoId(null);
              },
            }}
          />
        </div>
      </Show>

      {/* Open order editor */}
      <Show when={openPo()}>
        {(po) => (
          <div
            class="mt-6 rounded-md border border-border p-4"
            data-testid="po-editor"
            data-po-id={po().id}
            data-po-state={po().state ?? ""}
          >
            <div class="flex items-center justify-between">
              <h2 class="text-lg font-semibold">
                {t(msg`Purchase order`)}
                <Show when={po().reference}>
                  {" "}
                  <span class="text-muted-foreground">#{po().reference}</span>
                </Show>
              </h2>
              <span
                class="rounded-full border border-border px-2 py-0.5 text-xs"
                data-testid="po-editor-state"
              >
                {stateLabel(po().state)}
              </span>
            </div>

            {/* Add lines only in draft */}
            <Show when={coercePoState(po().state) === "draft"}>
              <div class="mt-4 flex flex-wrap items-end gap-3">
                <div class="flex flex-col gap-1">
                  <label class="text-xs text-muted-foreground">{t(msg`Product`)}</label>
                  <Select
                    class="w-48"
                    value={lineProduct()}
                    onChange={(v) => setLineProduct(v)}
                    data-testid="po-line-product-select"
                    aria-label={t(msg`Product`)}
                    placeholder={t(msg`Select product`)}
                    options={(products.data ?? []).map((p) => ({ value: p.id, label: p.name }))}
                    emptyAction={{
                      label: t(msg`Add a product`),
                      description: t(msg`No products yet`),
                      href: "/products",
                    }}
                  />
                </div>
                <div class="flex flex-col gap-1">
                  <label class="text-xs text-muted-foreground">{t(msg`Quantity`)}</label>
                  <input
                    class="w-28 h-10 rounded-md border border-border bg-field-background px-3 text-sm"
                    placeholder="0"
                    inputmode="decimal"
                    value={lineQty()}
                    onInput={(e) => setLineQty(e.currentTarget.value)}
                    data-testid="po-line-qty-input"
                  />
                </div>
                <div class="flex flex-col gap-1">
                  <label class="text-xs text-muted-foreground">{t(msg`Unit cost`)}</label>
                  <input
                    class="w-28 h-10 rounded-md border border-border bg-field-background px-3 text-sm"
                    placeholder="0"
                    inputmode="decimal"
                    value={linePrice()}
                    onInput={(e) => setLinePrice(e.currentTarget.value)}
                    data-testid="po-line-price-input"
                  />
                </div>
                <Button
                  onClick={() => void handleAddLine()}
                  disabled={!drizzle()}
                  data-testid="po-line-add-button"
                >
                  {t(msg`Add line`)}
                </Button>
              </div>
            </Show>

            {/* Lines */}
            <div class="mt-4" data-testid="po-line-list">
              <Show
                when={(openLines.data ?? []).length > 0}
                fallback={<p class="text-sm text-muted-foreground">{t(msg`No lines yet.`)}</p>}
              >
                <table class="w-full text-sm">
                  <thead>
                    <tr class="border-b border-border text-left text-muted-foreground">
                      <th class="py-2 font-medium">{t(msg`Product`)}</th>
                      <th class="py-2 font-medium text-right">{t(msg`Ordered`)}</th>
                      <th class="py-2 font-medium text-right">{t(msg`Received`)}</th>
                      <th class="py-2 font-medium text-right">{t(msg`Unit cost`)}</th>
                      <th class="py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    <For each={openLines.data ?? []}>
                      {(line) => (
                        <SwipeTableRow
                          class="border-b border-border/50"
                          data-testid="po-line-row"
                          data-product-id={line.product_id ?? ""}
                          data-pending={pendingQty({
                            product_id: line.product_id,
                            qty: line.qty,
                            price: line.price,
                            received_qty: line.received_qty,
                          })}
                          disabled={coercePoState(po().state) !== "draft"}
                          left={
                            coercePoState(po().state) === "draft"
                              ? {
                                  icon: Trash2,
                                  label: t(msg`Remove`),
                                  tone: "danger",
                                  exits: true,
                                  onCommit: () => void handleDeleteLine(line.id),
                                }
                              : undefined
                          }
                        >
                          <td class="py-2">{productName()(line.product_id ?? "")}</td>
                          <td class="py-2 text-right tabular-nums" data-testid="po-line-ordered">
                            {fromMinorQty(line.qty ?? 0)}
                          </td>
                          <td class="py-2 text-right tabular-nums" data-testid="po-line-received">
                            {fromMinorQty(line.received_qty ?? 0)}
                          </td>
                          <td class="py-2 text-right tabular-nums">
                            {fromMinorQty(line.price ?? 0)}
                          </td>
                          <td class="py-2 text-right">
                            <Show when={coercePoState(po().state) === "draft"}>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => void handleDeleteLine(line.id)}
                                data-testid="po-line-delete-button"
                              >
                                {t(msg`Remove`)}
                              </Button>
                            </Show>
                            <Show when={coercePoState(po().state) === "confirmed"}>
                              <ReceiveLineControl
                                onReceive={(qty) => void handleReceiveLine(line.id, qty)}
                              />
                            </Show>
                          </td>
                        </SwipeTableRow>
                      )}
                    </For>
                  </tbody>
                </table>
              </Show>
            </div>

            {/* Workflow actions */}
            <div class="mt-4 flex gap-2">
              <Show when={coercePoState(po().state) === "draft"}>
                <Button
                  onClick={() => void handleConfirm()}
                  disabled={!drizzle()}
                  data-testid="po-confirm-button"
                >
                  {t(msg`Confirm order`)}
                </Button>
              </Show>
              <Show when={coercePoState(po().state) === "confirmed"}>
                <Button
                  onClick={() => void handleReceiveAll()}
                  disabled={!drizzle()}
                  data-testid="po-receive-button"
                >
                  {t(msg`Receive all`)}
                </Button>
              </Show>
              <Button
                variant="ghost"
                onClick={() => {
                  setOpenPoId(null);
                }}
              >
                {t(msg`Close`)}
              </Button>
            </div>
          </div>
        )}
      </Show>

      {/* Order list */}
      <h2 class="mt-8 text-lg font-semibold">{t(msg`Purchase orders`)}</h2>
      <div class="mt-3" data-testid="po-list">
        <Show
          when={(orders.data ?? []).length > 0}
          fallback={
            <EmptyState
              icon={ShoppingCart}
              illustration={EmptyOrdersIllustration}
              title={t(msg`No purchase orders yet`)}
              description={t(msg`Create an order to buy stock from a vendor.`)}
            />
          }
        >
          <ul class="divide-y divide-border rounded-md border border-border">
            <For each={orders.data ?? []}>
              {(p) => (
                <li data-testid="po-row" data-po-id={p.id} data-po-state={p.state ?? ""}>
                  <SwipeRow
                    right={{
                      icon: FolderOpen,
                      label: t(msg`Open`),
                      tone: "primary",
                      onCommit: () => {
                        setOpenPoId(p.id);
                      },
                    }}
                    left={
                      coercePoState(p.state) === "draft"
                        ? {
                            icon: Trash2,
                            label: t(msg`Delete`),
                            tone: "danger",
                            exits: true,
                            onCommit: () => void handleDeletePo(p.id),
                          }
                        : undefined
                    }
                  >
                    <div class="flex items-center gap-3 px-4 py-3">
                      <ShoppingCart class="h-5 w-5 text-primary shrink-0" />
                      <div class="flex-1 min-w-0">
                        <p class="font-medium">
                          {vendorName2()(p.vendor_id)}
                          <Show when={p.reference}>
                            {" "}
                            <span class="text-muted-foreground">#{p.reference}</span>
                          </Show>
                        </p>
                      </div>
                      <span
                        class="rounded-full border border-border px-2 py-0.5 text-xs"
                        data-testid="po-row-state"
                      >
                        {stateLabel(p.state)}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setOpenPoId(p.id);
                        }}
                        data-testid="po-open-button"
                      >
                        {t(msg`Open`)}
                      </Button>
                      <Show when={coercePoState(p.state) === "draft"}>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => void handleDeletePo(p.id)}
                          data-testid="po-delete-button"
                        >
                          {t(msg`Delete`)}
                        </Button>
                      </Show>
                    </div>
                  </SwipeRow>
                </li>
              )}
            </For>
          </ul>
        </Show>
      </div>
    </PageContainer>
  );
}

/** Inline control for receiving a partial quantity from a line. */
function ReceiveLineControl(props: { onReceive: (qty: string) => void }) {
  const { t } = useI18n();
  const [qty, setQty] = createSignal("");
  return (
    <span class="inline-flex items-center gap-1">
      <input
        class="w-20 h-9 rounded-md border border-border bg-field-background px-2 text-sm"
        placeholder="0"
        inputmode="decimal"
        value={qty()}
        onInput={(e) => setQty(e.currentTarget.value)}
        data-testid="po-line-receive-input"
      />
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          props.onReceive(qty());
          setQty("");
        }}
        data-testid="po-line-receive-button"
      >
        {t(msg`Receive`)}
      </Button>
    </span>
  );
}

export const purchasesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/purchases",
  validateSearch: (search: Record<string, unknown>): { po?: string } => ({
    po: typeof search.po === "string" && search.po.trim() !== "" ? search.po : undefined,
  }),
  component: PurchasesPage,
});
